import React, { useEffect } from 'react'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import DropdownBar from '@ifca-root/react-component/src/components/SpecialBar/DropdownBar'
import { SearchBar } from '@ifca-root/react-component/src/components/SpecialBar/SearchBar'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import {
  useGetUsersByAccountAndSoftwareQuery,
  useGetResidualValueChangeStatusListingLazyQuery,
} from 'generated/graphql'
import { useFuseSearch } from 'helpers/Hooks/useSearch'
import { useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { ResidualValueEntryList } from './ResidualValueList/ResidualValueEntryList'
import { ResidualValuePostingList } from './ResidualValueList/ResidualValuePostingList'

export const ResidualValueMain = () => {
  const history = useHistory()
  const getSearch = JSON.parse(localStorage.getItem('searchFilter'))
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)

  const approvalStatusEnum = [
    { name: 'Draft', ID: 'ACTIVE' },
    { name: 'Submitted', ID: 'SUBMIT' },
    { name: 'Approved', ID: 'COMPLETED' },
    { name: 'Rejected', ID: 'REJECTED' },
    { name: 'All', ID: 'ALL' },
  ]

  const [postIDs, setPostIDs] = useState<Set<any>>(new Set([]))
  const [openExitConf, setOpenExitConf] = useState(false)
  const [TotalLength, setTotalLength] = useState(0)
  const [listStatus, setListStatus] = useState<string>(
    getSearch?.option ?? 'ACTIVE'
  )

  const [
    loadData,
    { loading: dataLoading, data: listData },
  ] = useGetResidualValueChangeStatusListingLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: ({ getResidualValueChangeStatusListing }) => {
      if (getResidualValueChangeStatusListing?.Data?.length > 0) {
        setOriginalListing([
          ...filteredList,
          ...getResidualValueChangeStatusListing?.Data,
        ])
      }
      setTotalLength(getResidualValueChangeStatusListing?.TotalLength)
    },
  })

  useEffect(() => {
    setOriginalListing([])
    loadData({
      variables: {
        CompanyID: CompanyID,
        StatusArr:
          listStatus === 'ALL'
            ? ['ACTIVE', 'SUBMIT', 'COMPLETED', 'REJECTED']
            : [listStatus],
        take: 30,
        skip: 0,
        searchValue: getSearch?.name || undefined,
      },
    })
  }, [listStatus, CompanyID])

  const {
    loading: UserLoading,
    data: { getUsersByAccountAndSoftware } = {
      getUsersByAccountAndSoftware: [],
    },
  } = useGetUsersByAccountAndSoftwareQuery({ fetchPolicy: 'network-only' })

  const {
    filteredList,
    handleSearch,
    setOriginalListing,
    originalList,
  } = useFuseSearch()

  const [timer, setTimer] = useState(null)
  const changeDelay = (searchValue: string) => {
    if (timer) {
      clearTimeout(timer)
      setTimer(null)
    }

    setTimer(
      setTimeout(() => {
        setOriginalListing([])
        loadData({
          variables: {
            CompanyID: CompanyID,
            StatusArr:
              listStatus === 'ALL'
                ? ['ACTIVE', 'SUBMIT', 'COMPLETED', 'REJECTED']
                : [listStatus],
            take: 30,
            skip: 0,
            searchValue: searchValue || undefined,
          },
        })
      }, 1000)
    )
  }

  const hasChanges = () => {
    return postIDs?.size !== 0
  }

  return (
    <>
      {(UserLoading || dataLoading) && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => {
          if (hasChanges() && listStatus === 'SUBMIT') {
            setOpenExitConf(true)
          } else {
            history.push(`/fixed-assets/${CompanyID}/assets-change`)
            localStorage.removeItem('searchFilter')
          }
        }}
        smTitle="Fixed Assets"
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Residual Value', current: true },
        ]}
      />

      <div className="search-filter with-dropdown-filter">
        <SearchBar
          borderRadius="4px"
          onChangeAction={e => {
            changeDelay(e?.target?.value)
            localStorage.setItem(
              'searchFilter',
              JSON.stringify({
                option: listStatus ?? 'ACTIVE',
                name: e?.target?.value,
              })
            )
          }}
          defaultValue={getSearch?.name ? getSearch?.name : ''}
          onCloseAction={() => {
            handleSearch('', [])
            setOriginalListing([])
            loadData({
              variables: {
                CompanyID: CompanyID,
                StatusArr:
                  listStatus === 'ALL'
                    ? ['ACTIVE', 'SUBMIT', 'COMPLETED', 'REJECTED']
                    : [listStatus],
                take: 30,
                skip: 0,
              },
            })
          }}
          isDefaultValue={!!getSearch?.name}
        />

        <DropdownBar
          options={approvalStatusEnum}
          setOptionID={setListStatus}
          borderRadius="4px"
          defaultOption={approvalStatusEnum
            ?.map(opt => opt?.ID)
            .indexOf(listStatus ?? 'ACTIVE')}
          searchFilter={getSearch}
        />
      </div>

      {(listStatus === 'ACTIVE' ||
        listStatus === 'COMPLETED' ||
        listStatus === 'REJECTED' ||
        listStatus === 'ALL') && (
        <ResidualValueEntryList
          filteredList={filteredList?.length > 0 ? filteredList : originalList}
          listLoading={dataLoading}
          listStatus={listStatus}
          userList={getUsersByAccountAndSoftware}
          setOriginalListing={setOriginalListing}
          fetchMore={loadData}
          hasMore={originalList?.length < TotalLength}
        />
      )}

      {listStatus === 'SUBMIT' && (
        <ResidualValuePostingList
          filteredList={filteredList?.length > 0 ? filteredList : originalList}
          listLoading={dataLoading}
          listStatus={listStatus}
          postIDs={postIDs}
          setPostIDs={setPostIDs}
          userList={getUsersByAccountAndSoftware}
          setOriginalListing={setOriginalListing}
          fetchMore={loadData}
          CompanyID={CompanyID}
        />
      )}

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/fixed-assets/${CompanyID}`)
        }}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error'}
      />
    </>
  )
}
